<template>
	<o-tour
		:ref="setup._tourOrderDetailsClient">
		<o-tour-step
			title="Détail d'une commande"
			size="lg"
			hide-finish>
			<template #actions="{ next }">
				<o-button
					size="xs"
					outline
					color="info"
					@click="setup.openDiscussion()">
					Contacter l'agence
				</o-button>
				<o-button
					size="xs"
					color="brand"
					@click="next">
					Suivant
				</o-button>
			</template>

			<p>
				Lorsqu'une commande a été <b>créée</b>, par vous-même ou votre agence d'emploi,
				vous pourrez accéder à <b>un détail</b> constitué de plusieurs éléments.
			</p>
			<ul>
				<li>
					Un <b>récapitulatif</b> de la commande en question.
				</li>
				<li>
					Une liste des <b>candidats validés</b>.
				</li>
				<li>
					Une liste des <b>candidats proposés</b>.
				</li>
			</ul>
			<o-alert color="info">
				Une commande créée <b>ne peut plus être modifiée</b> par vos soins.
				<br>Si certains éléments viennent à évoluer, faites en part à votre agence d'emploi
				via le <b>chat en ligne</b> Armado !
			</o-alert>
		</o-tour-step>

		<o-tour-step
			title="Récapitulatif de la commande"
			:target="setup.scrollToId.bind(setup, 'order-recap-card')"
			hide-finish>
			<p>
				Vous retrouverez l'ensemble des <b>informations saisies</b>.
			</p>
			<p>
				Sociétés concernées, dates, lieu de mission, profil recherché...
				<br>
				Ainsi que le <b>statut</b> actuel de la commande.
			</p>
		</o-tour-step>

		<o-tour-step
			title="Revenir en arrière"
			target="order-details__back"
			:next="setup.goToValidatedCandidates()"
			hide-finish>
			Cliquez ici si vous souhaitez <b>revenir en arrière</b> et retrouver <b>la liste</b> des commandes !
		</o-tour-step>

		<o-tour-step
			title="Intérimaires validés"
			:target="setup.getTarget.bind(setup)"
			size="lg"
			:previous="setup.goToOrderDetails()"
			:next="{
				callback: () => Bus.emit('OrderDetailsClientSetupService:change-tab', 'proposed'),
			}"
			hide-finish>
			<p>
				Vous remarquerez ici qu'il y a <b>un emplacement disponible
					par intérimaire souhaité</b>.
			</p>
			<p>
				Chaque fois que votre agence d'emploi validera <b>un profil</b> pour la mission,
				celui-ci viendra <b>compléter</b> l'un de ces emplacements !
			</p>
			<p>
				Petit exemple ⬇️
			</p>
			<o-alert class="validated-candidate-sample-content">
				<div class="flex fd-c">
					<div class="validated-candidate-proposal__empty-card col">
						<o-icon font-icon="icon-interface-user-multiple"/>
						<p>Intérimaire manquant <br> 1 / 2</p>
					</div>
					<div class="col col-xs-2 validated-candidate-sample-content__arrows">
						<o-icon font-icon="icon-interface-arrows-button-down"/>
						<o-icon font-icon="icon-interface-arrows-button-down"/>
					</div>
					<div class="col no-pointer-events">
						<order-client-proposal-card
							validated
							:order-client-proposal="setup.sampleProposal"/>
					</div>
				</div>
			</o-alert>
		</o-tour-step>

		<o-tour-step
			title="Candidats proposés"
			:target="setup.getProposedCandidates.bind(setup)"
			:previous="{
				callback: () => Bus.emit('OrderDetailsClientSetupService:change-tab', 'validated'),
			}"
			hide-finish>
			<p>
				À coté de l'onglet "Intérimaires validés", se trouve celui des <b>candidats proposés</b>.
			</p>
			<p>
				Lorsqu'un profil vous est partagé par votre agence d'emploi, il faudra vous <b>positionner</b> sur sa pertinence.
				<br>
				Pour ce faire, Armado vous permettra de <b>visualiser le profil</b>
				(<i>anonyme</i> 🥷)
				et de répondre à votre agence d'emploi en <b>acceptant</b> ou <b>refusant</b> la proposition.
			</p>
		</o-tour-step>

		<o-tour-step
			v-if="setup.canCopy"
			title="Création rapide"
			size="lg"
			hide-finish
			target="order-details__copy">
			<p>
				Lorsque vous faites appel à l'intérim, il peut s'agir d'une demande ponctuelle comme d'un besoin <b>récurrent</b>.
			</p>
			<p>
				Lorsque vous vous trouvez dans ce dernier cas, <b>simplicité</b> et <b>rapidité</b> sont les maitres mots !
			</p>
			<p>
				Afin de vous faire gagner du temps ⏰, nous avons prévu cette fonctionnalité : <i>Créer une nouvelle commande à partir de celle-ci</i>.
			</p>
			<p>
				Une action qui parle d'elle-même et viendra <b>préremplir</b> les étapes
				d'une nouvelle commande avec les informations de celle actuellement visitée.
			</p>
		</o-tour-step>

		<o-tour-step
			title="Discuter avec l'interlocuteur"
			target="order-details__discussion">
			Enfin, si vous avez des <b>questions</b> à poser à votre agence d'emploi
			ou des <b>informations complémentaires</b> à apporter, vous pourrez lancer une nouvelle
			<b>discussion contextuelle</b> à partir de cette icône.

			<template #actions="{ previous }">
				<o-button
					size="xs"
					outline
					color="info"
					@click="setup.openDiscussion()">
					Contacter l'agence
				</o-button>
				<o-button
					size="xs"
					outline
					@click="previous">
					Précédent
				</o-button>
			</template>
		</o-tour-step>
	</o-tour>
</template>

<script setup lang="ts">
import TourOrderDetailsClientSetupService from '@/setup/tour/TourOrderDetailsClientSetupService';
import OrderClientProposalCard from '@/components/order/OrderClientProposalCard.vue';
import { Bus } from '@orion.ui/orion';
const setup = new TourOrderDetailsClientSetupService();
</script>

<style scoped lang="less">

.validated-candidate-sample-content {
		background-color: rgba(243, 246, 251, 0.66);

		&__arrows {
			display: flex;
			margin: 1rem;
			flex-direction: column;
			justify-content: center;
			align-items: center;
		}
	}

	.no-pointer-events {
		pointer-events: none;
	}
.validated-candidate-proposal {
  margin-bottom: 3.1rem;

  &__progress-bar {
    width: 8.8rem;
  }

  &__empty-card {
    background: rgba(var(--rgb-grey), 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem;
    border-radius: 0.6rem;
    min-height: 12rem;

    .orion-icon {
      color: var(--grey);
      font-size: 3.1rem;
			margin-bottom: 0.2rem
    }

    p {
      margin: unset;
      color: var(--grey);
      font-style: italic;
      text-align: center;
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }
}
</style>
